import React, { memo, useEffect, useRef } from "react";
import RightBarSubscribe from "../RightBarSubscribe";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import { useProjectStore } from "../../stores/projectStore";
import classNames from "classnames";
import { StyledRightBarContainer } from "./styles";
import CircleIconButton from "../CircleIconButton";
import IconButton from "../IconButton";
import StickyNoteAnchor from "../StickyTour/StickyNoteAnchor";
import { AnchorIds } from "../StickyTour/TourTypes";
import { useSudoLayoutEventEmitter } from "../../lib/layout-events";

const RightBarContainer = memo(function RightBarContainer({
  editor,
  passedRef,
  loadSampleStory,
  existingCardsLoaded,
  totalDocuments,
  openFilter,
  getSelection,
  signal,
  needsFeedback,
  updateNeedsFeedback,
  updateHideEDUCards,
  demo,
  highlightInsert,
  tab,
  onChangeTab,
  loading,
  deleteCard,
  newCards,
  existingCards,
  favorites,
  favoritesByCards,
  onNextPage,
  insertEditor,
  favoritesStore,
  onCardFavorite,
  onCardUnfavorite,
  onDeleteFavorite,
  onFavoriteReorder,
  openBrainstorm,
  setMemory,
  memory,
  showFront,
  onDeleteCardItem,
  onCardStreamSettled,
  setCollapsed,
  rememberModifier,
}) {
  const subscriptionJSON = useSudowriteStore((state) => state.subscriptionJSON);

  const columnWidths = useProjectStore((state) => state.columnWidths);
  const project = useProjectStore((state) => state.project);
  const collapsed = columnWidths.right.collapsed;
  const importBlocksRightBar = project?.importStep && project.importStep !== "CANCELLED";
  const cardSetsRef = useRef(null);
  const emitSudoLayoutEvent = useSudoLayoutEventEmitter();

  // Expand the right bar with the requested panel (history or chat) focused
  const expandWithPanel = (focusEvent) => {
    if (importBlocksRightBar) return;
    setCollapsed(false);
    emitSudoLayoutEvent(focusEvent, null);
  };

  // Scroll when right bar is uncollapsed or new cards are added
  useEffect(() => {
    if (!collapsed && cardSetsRef.current) {
      cardSetsRef.current.scrollTop = 0;
    }
  }, [collapsed, newCards.length]);

  return (
    <StyledRightBarContainer
      className={classNames("rightbarcontainer-section section-component-container", { collapsed })}
      style={{ width: columnWidths.right.current, flexBasis: columnWidths.right.current, flexShrink: 0 }}
    >
      <div
        id="right"
        ref={passedRef}
      >
        <div className="collapse">
          <StickyNoteAnchor anchorId={AnchorIds.RIGHT_BAR_TOGGLE} />
          {collapsed ? (
            <>
              <CircleIconButton
                onClick={() => expandWithPanel("focus_history")}
                src="/assets/clock.svg"
                ariaLabel={importBlocksRightBar ? "Open History panel (unavailable while importing)" : "Open History panel"}
                hoverTipText={importBlocksRightBar ? "Unavailable while importing" : "History"}
              />
              <CircleIconButton
                onClick={() => expandWithPanel("focus_chat")}
                src="/assets/agentic-chat.svg"
                ariaLabel={importBlocksRightBar ? "Open Chat panel (unavailable while importing)" : "Open Chat panel"}
                hoverTipText={importBlocksRightBar ? "Unavailable while importing" : "Chat"}
              />
            </>
          ) : (
            <IconButton
              className="collapse-right-bar"
              onClick={() => setCollapsed(true)}
              src="/assets/collapse-sidebar.svg"
              ariaLabel="Collapse Right Bar"
            />
          )}
        </div>

        <RightBarSubscribe
          editor={editor}
          cardSetsRef={cardSetsRef}
          loadSampleStory={loadSampleStory}
          existingCardsLoaded={existingCardsLoaded}
          totalDocuments={totalDocuments}
          openFilter={openFilter}
          getSelection={getSelection}
          signal={signal}
          needsFeedback={needsFeedback}
          updateNeedsFeedback={updateNeedsFeedback}
          updateHideEDUCards={updateHideEDUCards}
          demo={demo}
          highlightInsert={highlightInsert}
          tab={tab}
          onChangeTab={onChangeTab}
          loading={loading}
          deleteCard={deleteCard}
          newCards={newCards}
          existingCards={existingCards}
          favorites={favorites}
          favoritesByCards={favoritesByCards}
          onNextPage={onNextPage}
          insertEditor={insertEditor}
          favoritesStore={favoritesStore}
          onCardFavorite={onCardFavorite}
          onCardUnfavorite={onCardUnfavorite}
          onDeleteFavorite={onDeleteFavorite}
          onFavoriteReorder={onFavoriteReorder}
          openBrainstorm={openBrainstorm}
          memory={memory}
          setMemory={setMemory}
          showFront={showFront}
          onDeleteCardItem={onDeleteCardItem}
          onCardStreamSettled={onCardStreamSettled}
          rememberModifier={rememberModifier}
        />
      </div>
    </StyledRightBarContainer>
  );
});

export default RightBarContainer;
